/**
 * Contexte des modules (briques) actifs pour la commune courante.
 *
 * Charge une fois /api/me/modules et expose isModuleActif(service).
 * Masquage purement cosmétique : empêche d'afficher une tuile / d'entrer sur
 * une brique désactivée. Les vraies barrières restent permissions + RLS.
 * Modèle opt-out : tout est actif tant que la commune ne désactive pas.
 */
import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth-context';

interface ModulesContextValue {
  /** Clés de modules désactivés pour la commune. */
  disabled: Set<string>;
  /** Clés de briques masquées côté administré (espace citoyen). */
  masquesAdministre: Set<string>;
  loading: boolean;
  /** True si le module (clé service) est actif pour la commune. */
  isModuleActif: (moduleKey?: string | null) => boolean;
  /** True si la brique est proposée côté administré (espace citoyen). */
  isVisibleAdministre: (key?: string | null) => boolean;
  refresh: () => Promise<void>;
}

const ModulesContext = createContext<ModulesContextValue | null>(null);

export function ModulesProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [disabled, setDisabled] = useState<Set<string>>(new Set());
  const [masquesAdministre, setMasquesAdministre] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!user) { setDisabled(new Set()); setMasquesAdministre(new Set()); setLoading(false); return; }
    setLoading(true);
    try {
      const { modules, masquesAdministre: masques } = await api.me.modules();
      setDisabled(new Set(modules.filter((m) => !m.actif).map((m) => m.moduleKey)));
      setMasquesAdministre(new Set(masques ?? []));
    } catch {
      // En cas d'échec, on ne masque rien (opt-out : tout reste visible).
      setDisabled(new Set());
      setMasquesAdministre(new Set());
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { void refresh(); }, [refresh]);

  const isModuleActif = useCallback(
    (moduleKey?: string | null) => !moduleKey || !disabled.has(moduleKey),
    [disabled]
  );
  const isVisibleAdministre = useCallback(
    (key?: string | null) => !key || !masquesAdministre.has(key),
    [masquesAdministre]
  );

  return (
    <ModulesContext.Provider value={{ disabled, masquesAdministre, loading, isModuleActif, isVisibleAdministre, refresh }}>
      {children}
    </ModulesContext.Provider>
  );
}

export function useModules(): ModulesContextValue {
  const ctx = useContext(ModulesContext);
  if (!ctx) {
    // Fallback sûr si le provider n'est pas monté : tout actif/visible.
    return { disabled: new Set(), masquesAdministre: new Set(), loading: false, isModuleActif: () => true, isVisibleAdministre: () => true, refresh: async () => {} };
  }
  return ctx;
}

/**
 * Garde de route : si le module est désactivé pour la commune, redirige
 * doucement vers le tableau de bord (cosmétique, pas une barrière de sécu).
 * Pendant le chargement de l'état des modules, on rend les enfants (évite un
 * flash de redirection sur une brique en fait active).
 */
export function RequireModule({ module: moduleKey, children }: { module: string; children: ReactNode }) {
  const { isModuleActif, loading } = useModules();
  if (!loading && !isModuleActif(moduleKey)) {
    return <Navigate to="/dashboard?module_indisponible=1" replace />;
  }
  return <>{children}</>;
}
